import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '@lib/useTypedTranslation';
import moment from 'moment';
import * as am4charts from '@amcharts/amcharts4/charts';

import { exportDevicesUtilizationChartAction, IDevicesUtilization, IPerformanceChartParams } from '../../../../services/core/performance';
import { useWorldAction } from '../../../../lib/useWorldAction';
import { getFormattedTitle, getPerformanceUtilizationDevicesPath, getTimeZoneOffset, getTitle } from './lib';
import { drawRelativeXAxisLabels } from '../../../../components/chart/lib';
import { getUserTimeZone } from '../../../../lib/timeZone';
import { Filter } from '../../../../components/timePeriodAndFilterPicker/useFilterPicker';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';

import './charts.css';
import { StackedColumnsDateChart } from '@components/chart/stackedColumnsDateChart';
import { formatTooltipDay } from '@components/chart/tooltips';

interface IProps {
  data: IDevicesUtilization[],
  filter: Filter,
  days: number,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart
}

export function CorePerformanceDevicesUtilization(props: IProps) {
  const { data, filter, days } = props;
  const exportChartData = useWorldAction(exportDevicesUtilizationChartAction);
  const { t } = useTranslation('performance');
  const title = getTitle(filter, days, t, 'DEVICES_USAGE', true);
  const history = useHistory();
  const routes = useWorldRoutes();
  const timeZoneOffset = getTimeZoneOffset(getUserTimeZone());

  const onBarClick = (dataContext: { deviceCount: number, date: number }) => {
    if (!dataContext || !(dataContext.deviceCount > 0)) {
      return;
    }
    const utc = moment.utc(dataContext.date).subtract(timeZoneOffset || 0, 'minutes').valueOf();
    history.push(getPerformanceUtilizationDevicesPath(routes, utc, filter));
  };

  const formattedTitle = getFormattedTitle(title);

  const onExportClicked = async (): Promise<string> => {
    const timeZone = getUserTimeZone();
    const params: IPerformanceChartParams = {
      days,
      homeLocationId: filter.homeLocation?.value.id,
      group: filter.group?.value,
      zoneId: filter.zone?.value.id,
      model: filter.model?.value,
      timeZone
    };
    return exportChartData(params);
  };

  return (
    <div className = 'core-performance-chart-section'>
      <StackedColumnsDateChart<any>
        data={data.map((data) => ({
          deviceCount: data.deviceCount,
          date: parseInt(data.date)
        }))}
        className={`core-performance-chart-devices-utilization`}
        export={{
          filename: `${formattedTitle}.${moment.utc().toISOString()}.csv`,
          onExportClicked: () => onExportClicked(),
          csv: true
        }}
        series={[{
          dataKey: 'deviceCount',
          description: t('DEVICES_USED'),
          visible: true,
          colour: '#2c678c',
        }]}
        title={title}
        days={props.days}
        dateAxisEndDuration={{ amount: '1', unit: 'day' }}
        xAxisType={'date'}
        yAxisType={'value'}
        formatTooltipDate={formatTooltipDay}
        cypressId={'corePerformanceDevicesUtilization'}
        hasLegend={true}
        drawXAxis={(_, xAxis) => drawRelativeXAxisLabels(xAxis as am4charts.DateAxis, days, timeZoneOffset)}
        onMount={props.onMount}
        onBarClick={onBarClick}
        useAxisTooltip
        bottomBorder={true}
      />
    </div>
  );
}
